'use client';

import { cn } from '@/lib/utils';
import { FieldHelp, type GuideTopicId } from '@/features/guided-help';

export interface OptionCard<T extends string> {
  value: T;
  label: string;
  description: string;
}

export interface OptionCardsFieldProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: OptionCard<T>[];
  disabled?: boolean;
  /** Título opcional encima de las tarjetas. */
  label?: string;
  /**
   * Guided-help topic for this instance. The component is generic and rendered
   * more than once per step, so the anchor cannot be hardcoded here: each call
   * site names the concept it is showing.
   */
  guideTopicId?: GuideTopicId;
}

/**
 * Selector de opciones mutuamente excluyentes en formato de tarjetas (radio).
 * Cada tarjeta muestra el nombre y una descripción, de modo que ambas
 * alternativas quedan visibles en paralelo (a diferencia de un checkbox).
 */
export function OptionCardsField<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  label,
  guideTopicId,
}: OptionCardsFieldProps<T>) {
  return (
    <div className="space-y-2" data-guide-id={guideTopicId}>
      {label && (
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {guideTopicId && <FieldHelp topicId={guideTopicId} />}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((opt) => {
          const isSelected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => !disabled && onChange(opt.value)}
              disabled={disabled}
              className={cn(
                'flex flex-col rounded-lg border-2 p-4 text-left transition-all',
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <span
                className={cn(
                  'font-medium leading-snug',
                  isSelected ? 'text-blue-700' : 'text-gray-900',
                )}
              >
                {opt.label}
              </span>
              <span
                className={cn(
                  'mt-1 text-xs',
                  isSelected ? 'text-blue-700/85' : 'text-muted-foreground',
                )}
              >
                {opt.description}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
